import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TbDotsVertical } from '../Icon/reactIcons';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.colors.general.text};

  &:hover {
    background-color: ${(p) => p.theme.colors.grey5};
  }
`;

const MenuDropdown = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 10000;
  animation: ${fadeIn} 0.15s ease-out;
  overflow: hidden;
`;

const MenuItem = styled.button<{ $isDanger?: boolean; $isMuted?: boolean }>`
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${(p) => (p.$isMuted ? '#f0f2f5' : 'white')};
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => (p.$isDanger ? '#C62828' : 'inherit')};

  &:hover {
    background-color: ${(p) => {
      if (p.$isDanger) return '#ffebee';
      if (p.$isMuted) return '#e4e7eb';
      return '#f8f9fa';
    }};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background-color: #e0e0e0;
  margin: 4px 0;
`;

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  isDanger?: boolean;
  /** Subtle gray background — e.g. Acknowledge (non-destructive dismiss) */
  isMuted?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  dividerBefore?: boolean;
  'data-testid'?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Custom trigger icon size */
  iconSize?: number;
}

interface Position {
  top: number;
  left: number;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ items, iconSize = 18 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 180; // min-width of dropdown

      // Position dropdown below the button, aligned to the right edge
      let left = rect.right - dropdownWidth;
      const top = rect.bottom + 4;

      // Ensure dropdown doesn't go off-screen to the left
      if (left < 8) {
        left = 8;
      }

      setPosition({ top, left });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickOnButton = buttonRef.current?.contains(target);
      const isClickOnDropdown = dropdownRef.current?.contains(target);

      if (!isClickOnButton && !isClickOnDropdown) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  const visibleItems = items.filter((item) => !item.hidden);

  // Don't render if no visible items
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Container>
      <MenuButton ref={buttonRef} onClick={handleToggle} type="button" aria-label="More actions">
        <TbDotsVertical size={iconSize} />
      </MenuButton>
      {isOpen &&
        createPortal(
          <MenuDropdown
            ref={dropdownRef}
            $top={position.top}
            $left={position.left}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {visibleItems.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.dividerBefore && idx > 0 && <MenuDivider />}
                <MenuItem
                  data-testid={item['data-testid']}
                  $isDanger={item.isDanger}
                  $isMuted={item.isMuted}
                  disabled={item.disabled}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if (!item.disabled) {
                      item.onClick();
                      // Defer unmount so the click does not fall through to the row beneath.
                      requestAnimationFrame(() => setIsOpen(false));
                    }
                  }}
                >
                  {item.icon}
                  {item.label}
                </MenuItem>
              </React.Fragment>
            ))}
          </MenuDropdown>,
          document.body
        )}
    </Container>
  );
};

export default ActionMenu;
